import FLAG from 'config/flag.singleton';
import HDR from 'constants/httpHeader.const';
import MS from 'constants/time/ms.const';
import { BeTimeoutError } from 'errors/be.error';
import { extendSession } from 'utils/cognito.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import isNotMissing from 'utils/predicates/any/isNotMissing.util';
import isFn from 'utils/predicates/function/isFn.util';
import isPositiveInteger from 'utils/predicates/number/isPositiveInteger.util';

const L = logger(['fetcher', 'FETCH-RELATED']);

const USE_EXTERNAL_SIGNAL = false;
const DEBUG_V1_FAIL = false;
const DEBUG_V4 = true;

const DEFAULT_TIMEOUT = 30 * MS.second; // default to 30s timeout to match BE gateway
const MIN_TIMEOUT = 1000;

const previous = {};

const isRepeated = (url, options) => {
  const method = options && options.method;

  if (url && url === previous.url && method && method === previous.method) {
    return true;
  }

  previous.url = url;
  previous.method = method;

  return false;
};

const createTimeoutPromiseV1 = ({ timeout, url, method }) => {
  if (!Number.isSafeInteger(timeout) || timeout < MS.second) {
    L.warn('createTimeoutPromise()', 'too short or invalid timeout:', timeout);
  }

  return new Promise((resolve, reject) =>
    setTimeout(
      () => reject(new Error(`fetch timeout after ${timeout / MS.second}s for ${String(method)} ${url}`)),
      timeout,
    ),
  );
};

const createTimeoutPromiseV3 = ({ delay, url, options }) =>
  new Promise(
    (resolve, reject) =>
      void delayed(delay, () => reject(new BeTimeoutError({ timeout: delay, url, method: options?.method }))),
  );

const v1 = (url, options, timeout = DEFAULT_TIMEOUT) => {
  if (isRepeated(url, options)) {
    // don't cancel the request, just log
    const endpoint = `${String(previous.method)} ${String(previous.url)}`;
    L.debug('v1()', 'repeated endpoint:', endpoint, 'with timeout:', timeout);
  }

  const method = options && options.method;
  const fetchPromise = fetch(url, options);
  const timeoutPromise = createTimeoutPromiseV1({ timeout, url, method });

  if (DEBUG_V1_FAIL) fetchPromise.catch((e) => void L.debug('v1()', `Failed fetch for ${String(method)} ${url}`, e));

  // the timeout might be too short in some cases and the original fetch() is never cancelled
  // this might cause repeated fetches to the back end
  return Promise.race([fetchPromise, timeoutPromise]);
};

const v3 = (url, options, timeout = DEFAULT_TIMEOUT) => {
  const isValidTimeout = isPositiveInteger(timeout) && timeout >= 1000;
  const delay = isValidTimeout ? timeout : DEFAULT_TIMEOUT;

  if (!isValidTimeout) L.warn('v3()', 'Invalid or too short timeout:', timeout, 'will use:', delay);
  const timeoutPromise = createTimeoutPromiseV3({ delay, url, options });

  const fetchPromise = fetch(url, options);

  // The timeout might be too short in some cases and the original fetch() is never cancelled,
  return Promise.race([fetchPromise, timeoutPromise]);
};

const determineTimeout = (timeout) => {
  const t = isPositiveInteger(FLAG.overrideFetchTimeout) ? FLAG.overrideFetchTimeout : timeout;

  if (isPositiveInteger(t) && t >= MIN_TIMEOUT) return t;

  if (void 1 !== t) L.warn('determineTimeout()', 'Invalid or too short:', t, 'will use:', DEFAULT_TIMEOUT);
  return DEFAULT_TIMEOUT;
};

const createSignal = (options) => {
  const FTAG = 'createSignal()';

  const external = USE_EXTERNAL_SIGNAL ? options?.signal : void 1;
  const timeout = AbortSignal.timeout(determineTimeout(options?.timeout));

  const ac = new AbortController();
  const abort = ac?.abort?.bind?.(ac);
  const own = ac?.signal;

  L.debug(FTAG, 'USE_EXTERNAL_SIGNAL:', USE_EXTERNAL_SIGNAL, 'external:', external);

  if (external instanceof AbortSignal) {
    external.addEventListener('abort', () => {
      // const r = external.reason;
      // const reason = null === r || void 1 === r || '' === fromAnyToTrimmed(r) ? 'cancelled' : r;
      call('createSignal() > addEventListener() > abort()', abort, external.reason);
    });
  } else if (isNotMissing(external)) {
    L.warn(FTAG, 'Bad outerSignal:', external, 'from options:', options);
  }

  return isFn(AbortSignal?.any) ? { signal: AbortSignal.any([own, timeout]), abort } : { signal: own, abort };
};

const v4 = (options) => {
  const FTAG = 'v4()';

  const { url, timeout, timeoutType, ...extra } = options ?? {};

  const { signal, abort } = createSignal(options);

  const response = extendSession(options).then((result) => {
    const { isTokenUpdated, mainToken: mt } = result ?? {};

    const headers = isTokenUpdated ? { ...(options?.headers ?? {}), [HDR.mt]: `Bearer ${mt}` } : options?.headers;
    const fetchOptions = { ...extra, signal, headers };

    if (isTokenUpdated) L.debug(FTAG, 'isTokenUpdated:', isTokenUpdated, 'mt:', mt, 'fetchOptions:', fetchOptions);

    try {
      if (DEBUG_V4) L.debug(FTAG, 'fetch init', options?.method, url);
      return fetch(url, fetchOptions);
    } finally {
      if (DEBUG_V4) L.debug(FTAG, 'fetch done', options?.method, url);
    }
  });

  return { response, abort, signal };
};

const fetcher = (options) => {
  const { v, url, timeout, ...extra } = options ?? {};

  if (v === 1) return v1(url, extra, timeout);
  if (v === 3) return v3(url, extra, timeout);

  return v4(options);
};

export default fetcher;
